import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { TournamentSchedulePlan } from "@bw/shared/api";
import { useMemo } from "react";

const readable = (value: string) =>
  value
    .replaceAll("_", " ")
    .replace(/^./, (letter) => letter.toUpperCase());

function scheduleTime(value: string, venueTimeZone: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      month: "short",
      timeZone: venueTimeZone,
    }).format(new Date(value));
  } catch {
    return "Time unavailable";
  }
}

type Props = {
  canEdit?: boolean;
  plan: TournamentSchedulePlan;
  pending: boolean;
  venueTimeZone: string;
  onPin: (decisionId: string, pinned: boolean) => Promise<void>;
};

export function TournamentSchedulePlanBoard({
  canEdit = true,
  plan,
  pending,
  venueTimeZone,
  onPin,
}: Props) {
  const courtGroups = useMemo(() => {
    const grouped = new Map<string, typeof plan.decisions>();
    [...plan.decisions]
      .sort((left, right) => left.start_at.localeCompare(right.start_at))
      .forEach((decision) => {
        const group = grouped.get(decision.court_label) ?? [];
        group.push(decision);
        grouped.set(decision.court_label, group);
      });
    return [...grouped.entries()];
  }, [plan.decisions]);

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: `repeat(${Math.min(Math.max(courtGroups.length, 1), 3)}, minmax(0, 1fr))`,
          },
        }}
      >
        {courtGroups.map(([court, decisions]) => (
          <Paper
            component="section"
            key={court}
            variant="outlined"
            sx={{ minWidth: 0, overflow: "hidden" }}
          >
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                bgcolor: "action.hover",
                borderBottom: 1,
                borderColor: "divider",
                justifyContent: "space-between",
                px: 1.5,
                py: 1.25,
              }}
            >
              <Typography component="h3" variant="subtitle1">
                {court}
              </Typography>
              <Chip
                label={`${decisions.length} ${decisions.length === 1 ? "match" : "matches"}`}
                size="small"
                variant="outlined"
              />
            </Stack>
            <Stack divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}>
              {decisions.map((decision) => (
                <Stack
                  key={decision.decision_id}
                  spacing={1}
                  sx={{ p: 1.5 }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "flex-start", justifyContent: "space-between" }}
                  >
                    <Stack spacing={0.35} sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600 }} variant="body2">
                        {decision.match_display_label}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{ alignItems: "center" }}
                      >
                        <AccessTimeOutlinedIcon color="action" fontSize="small" />
                        <Typography color="text.secondary" variant="caption">
                          {scheduleTime(decision.start_at, venueTimeZone)}–
                          {scheduleTime(decision.end_at, venueTimeZone)}
                        </Typography>
                      </Stack>
                    </Stack>
                    {decision.pinned ? (
                      <Chip
                        icon={<LockOutlinedIcon />}
                        label="Pinned"
                        size="small"
                        variant="outlined"
                      />
                    ) : null}
                  </Stack>
                  {plan.state === "draft" && canEdit ? (
                    <Button
                      disabled={pending}
                      onClick={() =>
                        void onPin(decision.decision_id, decision.pinned)
                      }
                      size="small"
                      sx={{ alignSelf: "flex-start" }}
                    >
                      {decision.pinned ? "Allow rescheduling" : "Keep here"}
                    </Button>
                  ) : null}
                </Stack>
              ))}
            </Stack>
          </Paper>
        ))}
      </Box>

      {plan.unscheduled.length ? (
        <Paper
          component="section"
          aria-label="Unscheduled matches"
          variant="outlined"
          sx={{ borderColor: "warning.main", p: 2 }}
        >
          <Stack spacing={1.25}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <ErrorOutlineOutlinedIcon color="warning" />
              <Stack spacing={0.25}>
                <Typography component="h3" variant="subtitle1">
                  Needs scheduling
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Resolve these constraints before publishing a complete order of play.
                </Typography>
              </Stack>
            </Stack>
            <Box
              component="ul"
              sx={{ display: "grid", gap: 1, listStyle: "none", m: 0, p: 0 }}
            >
              {plan.unscheduled.map((blocker) => (
                <Box
                  component="li"
                  key={`${blocker.match.kind}:${blocker.match.match_id}`}
                  sx={{
                    alignItems: "center",
                    bgcolor: "action.hover",
                    borderRadius: 1,
                    display: "flex",
                    gap: 1,
                    justifyContent: "space-between",
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Typography variant="body2">
                    {blocker.match_display_label}
                  </Typography>
                  <Chip color="warning" label={readable(blocker.code)} size="small" />
                </Box>
              ))}
            </Box>
          </Stack>
        </Paper>
      ) : null}
    </Stack>
  );
}
